
import React, { useLayoutEffect, useState, useRef } from "react";
import { EditIconSvg, DownloadIconSvg, DeleteIconSvg, ViewIngestIcon } from "./Svgs";

const ActionMenu = ({
  anchorEl,
  closeMenu,
  onRename,
  onDownload,
  onDelete,
  onViewFile,
  onViewAndIngest,
  item,
}) => {
  const menuRef = useRef(null);
  const [style, setStyle] = useState({ top: -9999, left: -9999, opacity: 0, pointerEvents: 'none' });

  useLayoutEffect(() => {
    if (!anchorEl || !menuRef.current) return;

    // Find the closest scrollable container (your table container)
    const tableContainer = anchorEl.closest('.overflow-auto.custom-scrollbar');

    const updatePosition = () => {
      const rect = anchorEl.getBoundingClientRect();
      const menuRect = menuRef.current.getBoundingClientRect();
      const menuHeight = menuRect.height || 200;
      
      const bottomSpace = window.innerHeight - rect.bottom;
      const openUpwards = bottomSpace < menuHeight;

      // Check if the button has scrolled under the header
      let isVisible = true;
      if (tableContainer) {
        const containerRect = tableContainer.getBoundingClientRect();
        // The header is usually around 36-40px tall. 
        // If the top of the button goes above the bottom of the header, hide the menu.
        const headerOffset = 40; 
        if (rect.top < containerRect.top + headerOffset || rect.bottom > containerRect.bottom) {
          isVisible = false;
        }
      }

      setStyle({
        top: openUpwards ? rect.top - menuHeight - 4 : rect.bottom + 4,
        left: rect.left - 90, 
        opacity: isVisible ? 1 : 0,
        pointerEvents: isVisible ? 'auto' : 'none',
      });
    };

    updatePosition();
    
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [anchorEl]);

  const statusLower = item?.status?.toLowerCase();
  const isIngested = statusLower === "ingested" || statusLower === "ready" || statusLower === "completed";
  // View & Ingest is actionable only once a file has finished uploading and is not
  // yet ingesting — i.e. exactly the "ready for review" state.
  const isReadyForReview = statusLower === "ready_for_review";

  return (
    <div
      ref={menuRef}
      className="fixed z-[9999] bg-white rounded-xl shadow-[0_4px_20px_-4px_rgba(0,0,0,0.15)] p-1.5 min-w-[140px] flex flex-col font-outfit border border-black/5 transition-opacity duration-75"
      style={style}
      onClick={(e) => e.stopPropagation()} 
      role="menu"
    >
      {/* View File */}
      <button
        onClick={() => {
          onViewFile?.(item);
          closeMenu?.();
        }}
        className="group flex items-center gap-3 px-2 py-[8px] rounded-lg cursor-pointer transition-all duration-150 w-full text-left hover:bg-gray-100 bg-transparent border-none"
      >
        <div className="w-5 h-5 flex-shrink-0 text-gray-500">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
          </svg>
        </div>
        <span className="text-sm font-medium text-gray-500">
          View File
        </span>
      </button>

      {/* Rename */}
      <button
        onClick={() => {
          onRename?.(item);
          closeMenu?.();
        }}
        className="group flex items-center gap-3 px-2 py-[8px] rounded-lg cursor-pointer transition-all duration-150 w-full text-left hover:bg-gray-100 bg-transparent border-none"
      >
        <div className="w-5 h-5 flex-shrink-0 text-gray-500">
          <EditIconSvg />
        </div>
        <span className="text-sm font-medium text-gray-500">
          Rename
        </span>
      </button>

      {/* Download */}
      <button
        onClick={() => {
          onDownload?.(item);
          closeMenu?.();
        }}
        className="group flex items-center gap-3 px-2 py-[8px] rounded-lg cursor-pointer transition-all duration-150 w-full text-left hover:bg-gray-100 bg-transparent border-none"
      >
        <div className="w-5 h-5 flex-shrink-0 text-gray-500">
          <DownloadIconSvg />
        </div>
        <span className="text-sm font-medium text-gray-500">
          Download
        </span>
      </button>

      {/* View & Ingest */}
      {!isIngested && (
        <button
          onClick={() => {
            if (!isReadyForReview) return;
            onViewAndIngest?.(item);
            closeMenu?.();
          }}
          disabled={!isReadyForReview}
          className={`group flex items-center gap-3 px-2 py-[8px] rounded-lg transition-all duration-150 w-full text-left bg-transparent border-none ${!isReadyForReview ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-gray-100"}`}
        >
          <div className="w-5 h-5 flex-shrink-0 text-gray-500">
            <ViewIngestIcon />
          </div>
          <span className="text-sm font-medium text-gray-500">
            View & Ingest
          </span>
        </button>
      )}

      {/* Delete */}
      <button
        onClick={() => {
          onDelete?.(item);
          closeMenu?.();
        }}
        className="group flex items-center gap-3 px-2 py-[8px] rounded-lg cursor-pointer transition-all duration-150 w-full text-left hover:bg-gray-100 bg-transparent border-none"
      >
        <div className="w-5 h-5 flex-shrink-0 text-gray-500">
          <DeleteIconSvg />
        </div>
        <span className="text-sm font-medium text-gray-500">
          Delete
        </span>
      </button>
    </div>
  );
};

export default ActionMenu;